import DOMPurify from 'dompurify';

const IMG_KEEP_ATTRS = new Set(['src', 'alt', 'width', 'height']);

/** 공지 전용 인스턴스: 훅이 다른 DOMPurify 사용처(시트 코드 등)에 섞이지 않게 분리 */
const noticePurifier = typeof window !== 'undefined' ? DOMPurify(window) : null;

if (noticePurifier) {
  noticePurifier.addHook('uponSanitizeAttribute', (node, data) => {
    if ((node as Element).tagName?.toLowerCase() === 'a' && data.attrName === 'target' && data.attrValue === '_blank') {
      data.forceKeepAttr = true;
    }
  });
  noticePurifier.addHook('afterSanitizeAttributes', (node) => {
    const el = node as Element;
    const tag = el.tagName?.toLowerCase();
    if (tag === 'img') {
      const src = (el.getAttribute('src') ?? '').trim();
      if (!/^https?:\/\//i.test(src)) {
        el.remove();
        return;
      }
      Array.from(el.attributes).forEach((attr) => {
        if (!IMG_KEEP_ATTRS.has(attr.name.toLowerCase())) el.removeAttribute(attr.name);
      });
      el.setAttribute('loading', 'lazy');
      el.setAttribute('referrerpolicy', 'no-referrer');
    }
    if (tag === 'a' && el.getAttribute('target') === '_blank') {
      el.setAttribute('rel', 'noopener noreferrer');
    }
  });
}

const NOTICE_FORBID_TAGS = [
  'style', 'form', 'input', 'button', 'textarea', 'select', 'option',
  'iframe', 'frame', 'object', 'embed', 'link', 'meta', 'base', 'svg', 'math',
];

/** 공지 본문 표시용: 허용 목록(DOMPurify) 기반으로 스크립트·이벤트 핸들러·위험 URL 제거 */
export function sanitizeSchoolNoticeHtml(html: string): string {
  const raw = String(html ?? '');
  if (!raw.trim() || !noticePurifier) return '';
  try {
    return noticePurifier.sanitize(raw, {
      FORBID_TAGS: NOTICE_FORBID_TAGS,
      ALLOWED_URI_REGEXP: /^(?:https?:|mailto:|tel:|#|\/)/i,
    });
  } catch {
    return '';
  }
}

const SKIP_INLINE_TAGS = new Set(['SCRIPT', 'STYLE', 'META', 'LINK', 'TITLE', 'HEAD', 'HTML', 'BR', 'HR', 'IMG']);

/** Word/Docs 등 `<style>` 기반 서식을 인라인으로 고정한 뒤 sanitize */
export function prepareSchoolNoticePasteHtml(html: string): string {
  const raw = String(html ?? '').trim();
  if (!raw) return '';
  if (typeof document === 'undefined') return sanitizeSchoolNoticeHtml(raw);

  let holder: HTMLDivElement | null = null;
  try {
    holder = document.createElement('div');
    holder.setAttribute(
      'style',
      'position:fixed;left:-10000px;top:0;width:720px;max-height:0;overflow:hidden;opacity:0;pointer-events:none;'
    );
    // 문서에 붙이기 전에 먼저 정리(붙여넣은 img onerror 등이 실행되지 않게). 서식 계산용 <style> 만 예외로 둔다
    holder.innerHTML = noticePurifier
      ? noticePurifier.sanitize(raw, { ADD_TAGS: ['style'], FORCE_BODY: true, ALLOWED_URI_REGEXP: /^(?:https?:|mailto:|tel:|#|\/)/i })
      : '';
    document.body.appendChild(holder);

    const root = holder.querySelector('body') ?? holder;
    root.querySelectorAll('*').forEach((node) => {
      if (!(node instanceof HTMLElement)) return;
      if (SKIP_INLINE_TAGS.has(node.tagName)) return;
      const cs = window.getComputedStyle(node);
      if (!node.style.fontSize && cs.fontSize) node.style.fontSize = cs.fontSize;
      if (!node.style.fontFamily && cs.fontFamily) node.style.fontFamily = cs.fontFamily;
      if (!node.style.fontWeight && cs.fontWeight && cs.fontWeight !== '400' && cs.fontWeight !== 'normal') {
        node.style.fontWeight = cs.fontWeight;
      }
      if (!node.style.fontStyle && cs.fontStyle && cs.fontStyle !== 'normal') {
        node.style.fontStyle = cs.fontStyle;
      }
      const deco = cs.textDecorationLine || cs.textDecoration;
      if (!node.style.textDecoration && deco && deco !== 'none') {
        node.style.textDecoration = deco;
      }
      if (!node.style.color && cs.color) node.style.color = cs.color;
      if (
        !node.style.textAlign &&
        cs.textAlign &&
        cs.textAlign !== 'start' &&
        cs.textAlign !== 'left' &&
        cs.textAlign !== 'justify-all'
      ) {
        node.style.textAlign = cs.textAlign;
      }
      if (
        !node.style.backgroundColor &&
        cs.backgroundColor &&
        cs.backgroundColor !== 'rgba(0, 0, 0, 0)' &&
        cs.backgroundColor !== 'transparent'
      ) {
        node.style.backgroundColor = cs.backgroundColor;
      }
    });

    return sanitizeSchoolNoticeHtml(root.innerHTML);
  } catch {
    return sanitizeSchoolNoticeHtml(raw);
  } finally {
    holder?.remove();
  }
}
